import { useForm } from "react-hook-form";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Globe, Mail, Phone, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAgent } from "@/hooks/agent/useAgents";
import { useUpdateClubAssociationOnboarding } from "@/hooks/onboarding/useClubAssociationOnboarding";
import { brandAssets } from "@/lib/brand/tokens";
import type { Agent } from "@/types/agent/agent";
import type { ClubAssociationOnboardingForm } from "@/types/customer-onboarding";
import { ClubAssociationFieldSlide } from "./ClubAssociationFieldSlide";

const EDIT_STEPS = [
  {
    id: "website" as const,
    label: "Club website",
    eyebrow: "WEBSITE",
    title: "Website",
    subtitle: "",
    placeholder: "northsideclub.org.nz",
    inputMode: "url" as const,
    autoComplete: "url",
    icon: Globe,
    hint: "Used as the public source for the setup.",
  },
  {
    id: "phone" as const,
    label: "Best phone number",
    eyebrow: "PHONE",
    title: "Phone",
    subtitle: "",
    placeholder: "+64 9 123 4567",
    inputMode: "tel" as const,
    autoComplete: "tel",
    icon: Phone,
    hint: "Used as the contact handoff number.",
  },
  {
    id: "email" as const,
    label: "Contact email",
    eyebrow: "EMAIL",
    title: "Email",
    subtitle: "",
    placeholder: "hello@northsideclub.org.nz",
    inputMode: "email" as const,
    autoComplete: "email",
    icon: Mail,
    hint: "Used for new enquiries and setup notes.",
  },
];

const ClubAssociationEditPage = () => {
  const { agentId = "" } = useParams();
  const { data: agent, isLoading } = useAgent(agentId);
  const updateDetails = useUpdateClubAssociationOnboarding();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ClubAssociationOnboardingForm>({
    values: getFormValues(agent),
  });

  const onSubmit = async (values: ClubAssociationOnboardingForm) => {
    if (!agent) return;
    await updateDetails.mutateAsync({ ...values, agent });
  };

  return (
    <div className="min-h-screen bg-genie-background text-[#222B45]">
      <div className="h-1 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]" />
      <header className="h-[64px] border-b border-genie-border bg-white/90 px-5 md:px-8 flex items-center">
        <div className="flex items-center gap-2">
          <img src={brandAssets.icon} alt="Help Genie" className="h-7 w-7 object-contain" />
          <span className="text-sm font-bold text-genie-logo">Help Genie</span>
          <span className="text-sm font-extrabold italic bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
            Voice
          </span>
        </div>
        <div className="flex-1" />
        <Link to="/clubs-onboarding" className="text-xs font-semibold text-gray-500 hover:text-genie-primary">
          Start again
        </Link>
      </header>

      <main className="px-5 py-8 md:px-8 md:py-10">
        <form onSubmit={handleSubmit(onSubmit)} className="mx-auto max-w-3xl">
          <Link to="/genies" className="inline-flex items-center text-sm font-semibold text-gray-500 hover:text-genie-primary">
            <ArrowLeft className="mr-2 h-4 w-4" />
            My genies
          </Link>
          <div className="mt-6">
            <p className="text-[11px] font-bold tracking-[1.6px] uppercase bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] bg-clip-text text-transparent">
              SIMPLE EDITS
            </p>
            <h1 className="mt-3 text-3xl font-bold text-[#222B45]">Keep the setup details current</h1>
            <p className="mt-4 text-sm md:text-base text-[#64748B] leading-relaxed">
              Update the basic contact details for this genie without opening the full workspace.
            </p>
          </div>

          <div className="mt-8 grid gap-4">
            {EDIT_STEPS.map((step) => (
              <ClubAssociationFieldSlide
                key={step.id}
                step={step}
                error={errors[step.id]?.message}
                registerProps={register(step.id, { required: `${step.label} is required.` })}
              />
            ))}
          </div>

          <div className="mt-6 flex justify-end">
            <Button
              type="submit"
              disabled={!agent || isLoading || updateDetails.isPending}
              className="h-11 rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white shadow-lg shadow-genie-primary/25"
            >
              <Save className="mr-2 h-4 w-4" />
              {updateDetails.isPending ? "Saving..." : "Save details"}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

function getFormValues(agent?: Agent): ClubAssociationOnboardingForm {
  const dynamicVariables =
    agent?.settings?.conversation_config?.agent?.dynamic_variables?.dynamic_variable_placeholders || {};
  const onboarding = agent?.info_links?.onboarding || {};

  return {
    website: onboarding.website || dynamicVariables.website || agent?.redirect_url || "",
    phone: onboarding.phone || dynamicVariables.phone || "",
    email: onboarding.email || dynamicVariables.email || agent?.report_recipients?.[0] || "",
  };
}

export default ClubAssociationEditPage;
